// Migrated from transition-web/src/components/SignedInSignUpForm.tsx
// Updated to use new design system with split-screen layout

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { SignedInViewProps } from '../types/SignedInTypes'
import { Button, Input, Checkbox } from '../interaxDesignSystemComponents'
import { useLogout } from '../hooks/useLogout'
import useAppState from '../hooks/useAppState'
import { useTranslation } from '../shared/hooks/useIntractionTranslate'
import SignInLayout from './SignInLayout'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../constants/colors'
import { PrivacyNoticeModal } from './PrivacyNotice/PrivacyNoticeModal'

const PRIVACY_NOTICE_CHECKBOX_ID = 'privacy-notice-acknowledgement'

export default function SignedInSignUpForm({
  isLoading,
  signedInState,
  onPressContinue,
  onClickBack
}: SignedInViewProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const logout = useLogout(() => navigate('/'))
  const { state } = useAppState()
  const [formData, setFormData] = useState({
    displayName: signedInState.data.displayName || state.user.displayName || '',
    phone: signedInState.data.phone || state.user.phone || '',
    privacyNoticeAcknowledged: false
  })
  const [errors, setErrors] = useState<{ displayName?: string; phone?: string; privacyNoticeAcknowledged?: string }>({})
  const [isPrivacyNoticeOpen, setIsPrivacyNoticeOpen] = useState(false)

  const handleSubmit = () => {
    const newErrors: { displayName?: string; phone?: string; privacyNoticeAcknowledged?: string } = {}

    if (!formData.displayName.trim()) {
      newErrors.displayName = t('auth.displayNameIsRequired')
    }

    // Phone is optional, but if provided, should be valid
    if (formData.phone && !/^\+?[\d\s\-()]+$/.test(formData.phone)) {
      newErrors.phone = t('auth.pleaseEnterValidPhone')
    }

    if (!formData.privacyNoticeAcknowledged) {
      newErrors.privacyNoticeAcknowledged = t('auth.mustAcknowledgePrivacyNotice')
    }

    setErrors(newErrors)

    if (Object.keys(newErrors).length === 0) {
      onPressContinue({
        ...signedInState,
        data: { ...signedInState.data, ...formData }
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field as keyof typeof errors]: undefined }))
    }
  }

  const isContinueDisabled = isLoading || !formData.displayName.trim() || !formData.privacyNoticeAcknowledged

  return (
    <SignInLayout backgroundImage="/assets/img/background_login.png">
      <Box className="space-y-8">
        {/* Back Button */}
        <Box className="flex justify-between items-center">
          <Button variant="link" onClick={onClickBack} className="text-thunder-grey hover:text-white">
            ← {t('auth.back')}
          </Button>
          <Button variant="link" onClick={logout} className="text-thunder-grey hover:text-white">
            {t('auth.signOut')}
          </Button>
        </Box>

        {/* Header */}
        <Box className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">{t('auth.completeYourProfile')}</h1>
          <p className="text-thunder-grey text-lg">{t('auth.addDetailsToPersonalize')}</p>
        </Box>

        {/* Sign Up Form */}
        <Box className="space-y-6">
          <Box>
            <Box className="text-white text-sm font-medium mb-2">{t('auth.displayNameLabel')}</Box>
            <Input
              value={formData.displayName}
              onChange={(e) => handleChange('displayName', e.target.value)}
              placeholder={t('auth.enterYourDisplayName')}
              disabled={isLoading}
              required
              error={!!errors.displayName}
              errorMessage={errors.displayName}
              fullWidth
              size="large"
            />
          </Box>

          <Box>
            <Box className="text-white text-sm font-medium mb-2">{t('auth.phoneNumberLabel')}</Box>
            <Input
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder={t('auth.enterYourPhoneNumber')}
              disabled={isLoading}
              error={!!errors.phone}
              errorMessage={errors.phone}
              fullWidth
              size="large"
            />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Checkbox
                id={PRIVACY_NOTICE_CHECKBOX_ID}
                className="mt-0.5"
                checked={formData.privacyNoticeAcknowledged}
                onChange={(checked) => handleChange('privacyNoticeAcknowledged', checked)}
              />
              <Typography
                component="label"
                htmlFor={PRIVACY_NOTICE_CHECKBOX_ID}
                sx={{
                  color: INTERAX_COLORS.white,
                  fontFamily: INTERAX_TYPOGRAPHY.body2.fontFamily,
                  fontWeight: INTERAX_TYPOGRAPHY.body2.fontWeight,
                  fontSize: INTERAX_TYPOGRAPHY.body2.fontSize,
                  lineHeight: INTERAX_TYPOGRAPHY.body2.lineHeight,
                  cursor: 'pointer'
                }}
              >
                {t('auth.iHaveReadAndUnderstoodThe')}{' '}
                <Box
                  component="button"
                  type="button"
                  onClick={() => setIsPrivacyNoticeOpen(true)}
                  sx={{
                    p: 0,
                    border: 0,
                    background: 'none',
                    font: 'inherit',
                    color: INTERAX_COLORS.sun,
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                    '&:focus-visible': { outline: `2px solid ${INTERAX_COLORS.sun}`, outlineOffset: '2px' }
                  }}
                >
                  {t('auth.privacyNoticeLink')}
                </Box>
              </Typography>
            </Box>
            {!!errors.privacyNoticeAcknowledged && (
              <Typography
                sx={{
                  color: INTERAX_COLORS.redError,
                  fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                  fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
                  fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                  lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight,
                  mt: 0.5,
                  ml: 4
                }}
              >
                {errors.privacyNoticeAcknowledged}
              </Typography>
            )}
          </Box>

          <Button variant="primary" fullWidth onClick={handleSubmit} disabled={isContinueDisabled} className="h-12">
            {isLoading ? t('auth.saving') : t('auth.continue')}
          </Button>
        </Box>
      </Box>

      <PrivacyNoticeModal
        isOpen={isPrivacyNoticeOpen}
        onClose={() => setIsPrivacyNoticeOpen(false)}
        onAcknowledge={() => handleChange('privacyNoticeAcknowledged', true)}
      />
    </SignInLayout>
  )
}
